import { falPlay } from 'shared/@generated/icons/falPlay';
import { Platform, TouchableOpacity, View } from 'react-native';
import { useRef } from 'react';
import { Props, Animal, Breeder, MediaAsset, MediaAssetType } from 'shared/@types';
import { useTranslation } from 'react-i18next';
import { H2 } from 'shared/components/title';
import { PageBuilder } from 'shared/library/page-builder';
import { apiRoute } from 'shared/library/api-route';
import { Text } from 'shared/components/text';
import { Image } from 'shared/components/image';
import { Video } from 'shared/components/video';
import {
  GalleryFullscreenModal,
  GalleryFullscreenModalRef,
} from 'shared/components/gallery/gallery-fullscreen-modal';
import { Icon } from 'shared/components/icon';
import { getMediaAssetVideoPosterUrl } from 'shared/library/media-asset-urls';
import { useFormatter } from 'shared/library/use-formatter';

type BreederPhotoSourceType =
  | 'breeder'
  | 'breeder_pet'
  | 'breeder_post'
  | 'listing'
  | 'pet'
  | 'unknown';

type BreederPhotoSource = {
  type: BreederPhotoSourceType;
  title: string | null;
  subtitle: string | null;
  birth_date: string | null;
};

type BreederPhotoMediaAsset = MediaAsset & {
  source?: BreederPhotoSource;
};

interface BreederProfilePhotosScreenProps extends Props {
  breeder: Breeder;
  mediaAssets: BreederPhotoMediaAsset[];
}

const BreederProfilePhotosScreen = ({ breeder, mediaAssets }: BreederProfilePhotosScreenProps) => {
  const { t } = useTranslation('screens');
  const { formatDate } = useFormatter();
  const fullscreenModalRef = useRef<GalleryFullscreenModalRef>(null);

  function openVideo(index: number) {
    fullscreenModalRef.current?.open(index);
  }

  return (
    <View className={'gap-4 md:gap-6'}>
      <H2 title={t('breeders.show_breeder.photos')} marginTop={false} marginBottom={false} />

      {mediaAssets.length > 0 ? (
        <View className={'grid gap-5 md:grid-cols-3 md:gap-6'}>
          {mediaAssets.map((mediaAsset, index) => (
            <BreederPhotoCard
              key={mediaAsset.uuid}
              breeder={breeder}
              mediaAsset={mediaAsset}
              sourceText={getPhotoSourceText(mediaAsset, breeder, formatDate, t)}
              priority={index === 0}
              featured={index === 0}
              playVideoLabel={t('components:gallery.play_video')}
              onVideoPress={() => openVideo(index)}
            />
          ))}
        </View>
      ) : (
        <Text className={'text-brand-brown-dark'}>
          {t('breeders.show_breeder.no_photos_added')}
        </Text>
      )}

      {Platform.OS !== 'web' && (
        <GalleryFullscreenModal
          ref={fullscreenModalRef}
          items={mediaAssets}
          defaultAlt={breeder.name}
          aspect={'video'}
        />
      )}
    </View>
  );
};

BreederProfilePhotosScreen.pageBuilder = new PageBuilder<
  BreederProfilePhotosScreenProps,
  { breeder: string }
>().request(({ req, params }) =>
  req<{ breeder: Breeder; media_assets: BreederPhotoMediaAsset[] }>(
    apiRoute('breeders.photos', { breeder: params.breeder }),
    {
      compact: true,
    },
  ).then((data) => ({
    breeder: data.breeder,
    mediaAssets: data.media_assets,
  })),
);

function BreederPhotoCard({
  breeder,
  mediaAsset,
  sourceText,
  priority,
  featured,
  playVideoLabel,
  onVideoPress,
}: {
  breeder: Breeder;
  mediaAsset: BreederPhotoMediaAsset;
  sourceText: string;
  priority: boolean;
  featured: boolean;
  playVideoLabel: string;
  onVideoPress: () => void;
}) {
  const videoPoster =
    mediaAsset.type === MediaAssetType.Video ? getMediaAssetVideoPosterUrl(mediaAsset) : undefined;

  return (
    <View className={'gap-2'}>
      <View className={'bg-brand-cream overflow-hidden rounded-lg'}>
        {mediaAsset.type === MediaAssetType.Video && Platform.OS === 'web' ? (
          <Video
            src={mediaAsset.url}
            containerClassName={'aspect-4/3 w-full'}
            videoClassName={'object-cover'}
            controls={true}
            autoPlay={false}
            muted={false}
            loop={false}
            sizeType={'large'}
          />
        ) : mediaAsset.type === MediaAssetType.Video ? (
          <TouchableOpacity
            activeOpacity={0.9}
            accessibilityRole={'button'}
            accessibilityLabel={playVideoLabel}
            accessibilityHint={mediaAsset.description ?? breeder.name}
            className={'aspect-4/3 w-full'}
            onPress={onVideoPress}
          >
            {videoPoster ? (
              <Image
                src={videoPoster}
                alt={mediaAsset.description ?? breeder.name}
                containerClassName={'size-full'}
                imageClassName={'object-cover'}
                sizeType={featured ? 'large' : 'card'}
                aspectRatio={4 / 3}
                priority={priority}
              />
            ) : (
              <View className={'size-full bg-black'} />
            )}

            <View className={'absolute inset-0 items-center justify-center bg-black/20'}>
              <View className={'rounded-full bg-white/90 p-3'}>
                <Icon icon={falPlay} className={'text-brand-brown-dark size-5'} />
              </View>
            </View>
          </TouchableOpacity>
        ) : (
          <Image
            src={mediaAsset}
            alt={mediaAsset.description ?? breeder.name}
            containerClassName={'aspect-4/3 w-full'}
            imageClassName={'object-cover'}
            sizeType={featured ? 'large' : 'card'}
            aspectRatio={4 / 3}
            priority={priority}
          />
        )}
      </View>

      <View className={'gap-1'}>
        <Text className={'text-brand-brown-dark text-sm font-semibold md:text-base'}>
          {sourceText}
        </Text>

        {mediaAsset.description && (
          <Text className={'text-brand-brown-dark/70 text-sm'}>{mediaAsset.description}</Text>
        )}
      </View>
    </View>
  );
}

function getPhotoSourceText(
  mediaAsset: BreederPhotoMediaAsset,
  breeder: Breeder,
  formatDate: ReturnType<typeof useFormatter>['formatDate'],
  t: ReturnType<typeof useTranslation>['t'],
) {
  const source = mediaAsset.source;

  if (!source) {
    return t('breeders.show_breeder.photo_source_unknown');
  }

  if (source.type === 'breeder') {
    return t('breeders.show_breeder.photo_source_breeder');
  }

  if (source.type === 'breeder_pet') {
    return t(
      breeder.animal === Animal.Dog
        ? 'breeders.show_breeder.photo_source_breeder_pet_dog'
        : 'breeders.show_breeder.photo_source_breeder_pet_cat',
      { name: source.title },
    );
  }

  if (source.type === 'listing') {
    if (source.birth_date) {
      return t('breeders.show_breeder.photo_source_listing_with_date', {
        name: source.title,
        date: formatDate(source.birth_date),
      });
    }

    return t('breeders.show_breeder.photo_source_listing', { name: source.title });
  }

  if (source.type === 'pet') {
    if (source.subtitle) {
      return t('breeders.show_breeder.photo_source_pet_with_litter', {
        name: source.title,
        litter: source.subtitle,
      });
    }

    return t('breeders.show_breeder.photo_source_pet', { name: source.title });
  }

  if (source.type === 'breeder_post') {
    return t('breeders.show_breeder.photo_source_post', { name: source.title });
  }

  return t('breeders.show_breeder.photo_source_unknown');
}

export { BreederProfilePhotosScreen };
export type { BreederProfilePhotosScreenProps };
